import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  AppBar,
  Badge,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import {
  NotificationsOutlined as NotificationsIcon,
  Check as CheckIcon,
  Menu as MenuIcon,
  PaletteOutlined as PaletteIcon,
} from '@mui/icons-material';
import { useColorScheme } from '@mui/material/styles';

import { NotificationsPopover } from 'components';
import { themeOptions } from '../../../theme';
import { useThemePreset } from '../../../theme/ThemePresetProvider';
//import { sseFetchData } from 'utils/callApi';
import { callApi } from 'utils';

const TopBar = ({children, hideNavBar, onOpenNavBarDrawer, styles}) => {
  const notificationsRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const { mode, setMode } = useColorScheme();
  const { themePreset, setThemePreset } = useThemePreset();

  useEffect(() => {
    //sseFetchData('/notifications', (msgs) => {
    //  setNotifications(prev => [...prev, ...msgs].slice(-30));
    //});
    let from = localStorage.getItem('notification') || '';
    const itvl = setInterval(() => {
      callApi(`/notifications?from=${encodeURIComponent(from)}`)
        .then(msgs => {
          if (msgs?.length) {
            setNotifications(prev => msgs.length >= 8 ? msgs : [...msgs, ...prev].slice(0, 8));
            from = msgs[0].created_at;
            localStorage.setItem("notification", from);
          }
        })
        .catch(console.log);
    }, 300_000);

    return () => clearInterval(itvl);
  }, []);

  const handleNotificationsOpen = () => setNotificationsOpen(true);
  const handleNotificationsClose = () => {
    setNotificationsOpen(false);
    setNotifications(prev => prev.map(n => ({...n, read: true})));
  }

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const navBarHidden = ({always: true, never: false, auto: !lgUp})[hideNavBar];
  const darkMode = mode === 'dark';
  const communityTheme = themePreset !== 'classic';
  const toggleColorMode = () => setMode(darkMode ? 'light' : 'dark');
  const closeThemeMenu = () => setThemeMenuAnchor(null);
  const selectTheme = themeName => {
    setThemePreset(themeName);
    closeThemeMenu();
  };

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        ...styles
      }}
      color="primary"
      enableColorOnDark={communityTheme}
    >
      <Toolbar
        variant="dense"
        sx={{minHeight: 48, overflow: 'hidden', paddingLeft: 1}}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: '0 0 86px',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          {navBarHidden ? (
            <IconButton
              color="inherit"
              onClick={onOpenNavBarDrawer}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Typography
              component={RouterLink}
              to="/groups"
              sx={{
                color: 'primary.contrastText',
                cursor: 'pointer',
                fontSize: 15,
                fontWeight: 800,
                lineHeight: 1,
                textDecoration: 'none',
              }}
            >
              ArcMgr
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
        <IconButton
          sx={{flex: '0 0 auto', marginLeft: 1}}
          color="inherit"
          onClick={handleNotificationsOpen}
          ref={notificationsRef}
        >
          <Badge badgeContent={unreadNotifications} color="secondary">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <Tooltip title="切换主题">
          <IconButton
            aria-controls={themeMenuAnchor ? 'theme-menu' : undefined}
            aria-expanded={themeMenuAnchor ? 'true' : undefined}
            aria-haspopup="menu"
            aria-label="切换主题"
            color="inherit"
            onClick={event => setThemeMenuAnchor(event.currentTarget)}
            sx={{flex: '0 0 auto'}}
          >
            <PaletteIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={themeMenuAnchor}
          id="theme-menu"
          MenuListProps={{'aria-label': '主题'}}
          onClose={closeThemeMenu}
          open={Boolean(themeMenuAnchor)}
        >
          {themeOptions.map(option => {
            const selected = themePreset === option.value;
            return (
              <MenuItem
                key={option.value}
                onClick={() => selectTheme(option.value)}
                selected={selected}
              >
                <ListItemIcon>
                  <CheckIcon
                    fontSize="small"
                    sx={{visibility: selected ? 'visible' : 'hidden'}}
                  />
                </ListItemIcon>
                {option.label}
              </MenuItem>
            );
          })}
        </Menu>
        <Tooltip title={darkMode ? '关闭黑色主题' : '开启黑色主题'}>
          <Switch
            checked={darkMode}
            color="default"
            inputProps={{ 'aria-label': '黑色主题' }}
            onChange={toggleColorMode}
            size="small"
            sx={{ marginLeft: 0.5 }}
          />
        </Tooltip>
      </Toolbar>
      <NotificationsPopover
        anchorEl={notificationsRef.current}
        notifications={notifications}
        onClose={handleNotificationsClose}
        open={notificationsOpen}
      />
    </AppBar>
  );
};

TopBar.propTypes = {
  children: PropTypes.node,
  hideNavBar: PropTypes.oneOf(['always', 'never', 'auto']),
  onOpenNavBarDrawer: PropTypes.func,
  styles: PropTypes.object,
};

export default TopBar;
